import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, LogOut, ChevronDown, Shield, Home } from 'lucide-react';

import { AuthContext } from '@/App';
import { toast } from 'sonner';
import { AlertCircle, X } from 'lucide-react';


const UserMenu = ({ user, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const { axiosInstance } = React.useContext(AuthContext);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [issueForm, setIssueForm] = useState({ issue_type: 'Error Login', description: '', priority: 'Sedang', contact_info: '' });

  const handleReportIssue = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axiosInstance.post('/issues', issueForm);
      toast.success('Kendala berhasil dilaporkan. Terima kasih!');
      setShowIssueModal(false);
      setIssueForm({ issue_type: 'Error Login', description: '', priority: 'Sedang', contact_info: '' });
      setIsOpen(false);
    } catch (error) {
      toast.error('Gagal melaporkan kendala');
    } finally {
      setIsSubmitting(false);
    }
  };


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAdminDashboard = () => {
    setIsOpen(false);
    navigate('/admin');
  };

  const handleUserDashboard = () => {
    setIsOpen(false);
    navigate('/dashboard');
  };

  const handleLogout = () => {
    setIsOpen(false);
    onLogout();
  };

  // Check if currently on admin page
  const isOnAdminPage = location.pathname === '/admin';

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-green-50 transition-colors"
        data-testid="user-menu-button"
      >
        {/* Avatar or User Icon */}
        {user?.picture ? (
          <img 
            src={user.picture} 
            alt={user.name}
            className="w-8 h-8 rounded-full border-2 border-green-500"
          />
        ) : (
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <User className="w-4 h-4 text-green-600" />
          </div>
        )}
        
        {/* Name - Hidden on mobile */}
        <span className="hidden md:block text-sm font-medium text-gray-700">
          {user?.name || 'User'}
        </span>
        
        {/* Dropdown icon - Hidden on mobile */}
        <ChevronDown className={`hidden md:block w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            {user?.role === 'admin' && (
              <span className="inline-block mt-2 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">
                Admin
              </span>
            )}
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {/* ✅ Admin Dashboard Menu - Only show for admin or Pimpinan */}
            {(user?.role === 'admin' || user?.position === 'Pimpinan') && (
              <>
                {!isOnAdminPage && (
                  // Show "Admin Dashboard" if currently on user page
                  <button
                    onClick={handleAdminDashboard}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2 transition-colors"
                    data-testid="admin-dashboard-button"
                  >
                    <Shield className="w-4 h-4 text-green-600" />
                    <span>Admin Dashboard</span>
                  </button>
                )}
                
                {/* Divider */}
                <div className="my-1 border-t border-gray-100"></div>
              </>
            )}

            <button
              onClick={() => { setIsOpen(false); setShowIssueModal(true); }}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2 transition-colors border-t border-gray-100"
            >
              <AlertCircle className="w-4 h-4" />
              <span>Lapor Kendala</span>
            </button>
            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2 transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span>Keluar</span>
            </button>
          </div>
        </div>
      )}

      {/* Lapor Kendala Modal */}
      {showIssueModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden relative">
            <button onClick={() => setShowIssueModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
            <div className="px-6 py-4 border-b border-gray-100 bg-red-50">
              <h3 className="text-lg font-bold text-red-900 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                Lapor Kendala Sistem
              </h3>
              <p className="text-xs text-red-600 mt-1">Tim IT akan segera memeriksa laporan Anda.</p>
            </div>
            <form onSubmit={handleReportIssue} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kategori Kendala</label>
                <select 
                  className="w-full border border-gray-300 rounded-md p-2"
                  value={issueForm.issue_type}
                  onChange={(e) => setIssueForm({...issueForm, issue_type: e.target.value})}
                >
                  <option value="Error Login">Error Login / Akses Ditolak</option>
                  <option value="Data Tidak Muncul">Data Tidak Muncul / Hilang</option>
                  <option value="Fitur Tidak Berfungsi">Tombol / Fitur Tidak Berfungsi</option>
                  <option value="Lainnya">Lainnya</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prioritas</label>
                  <select 
                    className="w-full border border-gray-300 rounded-md p-2"
                    value={issueForm.priority}
                    onChange={(e) => setIssueForm({...issueForm, priority: e.target.value})}
                  >
                    <option value="Rendah">Rendah</option>
                    <option value="Sedang">Sedang</option>
                    <option value="Tinggi">Tinggi (Kritis)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kontak WA/Email (Opsional)</label>
                  <input 
                    type="text" 
                    className="w-full border border-gray-300 rounded-md p-2" 
                    placeholder="0812... / email@..."
                    value={issueForm.contact_info} 
                    onChange={(e) => setIssueForm({...issueForm, contact_info: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Deskripsi Detail</label>
                <textarea 
                  required 
                  className="w-full border border-gray-300 rounded-md p-2" 
                  rows="4"
                  placeholder="Jelaskan secara spesifik apa yang terjadi, di halaman mana, dan pesan error apa yang muncul..."
                  value={issueForm.description} 
                  onChange={(e) => setIssueForm({...issueForm, description: e.target.value})}
                ></textarea>
              </div>
              <div className="flex gap-3 pt-4 border-t">
                <button type="button" className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50" onClick={() => setShowIssueModal(false)}>Batal</button>
                <button type="submit" disabled={isSubmitting} className={`px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 flex items-center space-x-2 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}>{isSubmitting ? 'Mengirim...' : 'Kirim Laporan'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default UserMenu;